const DB_NAME = "sv-offline-pos"
const DB_VERSION = 1
const SNAPSHOT_STORE = "snapshots"
const OUTBOX_STORE = "sale-outbox"

const openDb = () =>
	new Promise((resolve, reject) => {
		if (!process.client || !window.indexedDB) return reject(new Error("Offline storage is unavailable"))
		const request = window.indexedDB.open(DB_NAME, DB_VERSION)
		request.onupgradeneeded = () => {
			const db = request.result
			if (!db.objectStoreNames.contains(SNAPSHOT_STORE)) db.createObjectStore(SNAPSHOT_STORE, { keyPath: "key" })
			if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
				const store = db.createObjectStore(OUTBOX_STORE, { keyPath: "offline_sale_id" })
				store.createIndex("created_at", "created_at")
				store.createIndex("status", "status")
			}
		}
		request.onsuccess = () => resolve(request.result)
		request.onerror = () => reject(request.error)
	})

const run = async (storeName, mode, work) => {
	const db = await openDb()
	return new Promise((resolve, reject) => {
		const tx = db.transaction(storeName, mode)
		const result = work(tx.objectStore(storeName))
		tx.oncomplete = () => { db.close(); resolve(result?.result) }
		tx.onerror = () => { db.close(); reject(tx.error) }
	})
}

const snapshotKey = (branchId, posId) => `snapshot:${branchId}:${posId}`
export const isOfflineCatalogStale = (
	snapshot,
	freshTotal,
	now = Date.now(),
	ttlMs = 24 * 60 * 60 * 1000,
) => {
	const catalogRefreshedAt = Date.parse(snapshot?.catalog_refreshed_at || "")
	return (
		!Number.isFinite(catalogRefreshedAt) ||
		(snapshot?.products || []).length !== Number(freshTotal || 0) ||
		now - catalogRefreshedAt > ttlMs
	)
}
const normalized = (value) => String(value || "").trim().toLowerCase()

export const filterOfflineProducts = (products, query, branchId) => {
	const search = normalized(query)
	if (!search) return products || []
	return (products || []).filter((product) => {
		if (normalized(product.product_name).includes(search)) return true
		const entries = product.has_variants ? product.variants : product.branches
		return (entries || []).some((entry) =>
			String(entry.branch || "") === String(branchId || "") &&
			[entry.sku, entry.barcode].some((value) => normalized(value).includes(search)),
		)
	})
}

export const filterOfflineCustomers = (customers, firstName, lastName) => {
	const first = normalized(firstName)
	const last = normalized(lastName)
	return (customers || []).filter((customer) =>
		(!first || normalized(customer.first_name).includes(first)) &&
		(!last || normalized(customer.last_name).includes(last)),
	)
}

export const filterOfflineReceipts = (receipts, from, to, serial, page = 1, pageSize = 12) => {
	const serialQuery = normalized(serial)
	const start = from ? new Date(`${from}T00:00:00`).getTime() : -Infinity
	const end = to ? new Date(`${to}T23:59:59.999`).getTime() : Infinity
	const filtered = (receipts || []).filter((receipt) => {
		const createdAt = new Date(receipt.created_at).getTime()
		return createdAt >= start && createdAt <= end &&
			(!serialQuery || normalized(receipt.serial).startsWith(serialQuery))
	})
	const offset = (Math.max(Number(page) || 1, 1) - 1) * pageSize
	return { receipts: filtered.slice(offset, offset + pageSize), total: filtered.length }
}

export const offlineSaleToReceipt = (sale) => ({
	_id: `offline:${sale.offline_sale_id}`,
	serial: sale.temporary_receipt,
	created_at: sale.created_at,
	grand_total: Number(sale.grand_total || 0),
	applied_charges: sale.applied_charges || [],
	items: sale.items || [],
	payment_type: sale.payment_type || "cash",
	payment_splits: sale.payment_splits || [],
	change: Number(sale.change || 0),
	cash_received: Number(sale.cash_received || 0),
	receipt_type: "Pending sync",
	offline: true,
	cancelled: false,
})
// Pinia/Vue state is reactive Proxy data, which IndexedDB cannot clone directly.
// Offline records intentionally contain only JSON-safe business snapshots.
const toPlainData = (value) => JSON.parse(JSON.stringify(value))
const notifyOutboxChanged = () => {
	if (process.client) window.dispatchEvent(new Event("sv:offline-sales-change"))
}

export const getOfflineSnapshot = async (branchId, posId) =>
	run(SNAPSHOT_STORE, "readonly", (store) => store.get(snapshotKey(branchId, posId)))

export const saveOfflineSnapshot = async (snapshot) => {
	const plainSnapshot = toPlainData(snapshot)
	return run(SNAPSHOT_STORE, "readwrite", (store) => store.put({
		...plainSnapshot,
		key: snapshotKey(plainSnapshot.branch_id, plainSnapshot.pos_id),
	}))
}

export const clearOfflineSnapshot = async (branchId, posId) =>
	run(SNAPSHOT_STORE, "readwrite", (store) => store.delete(snapshotKey(branchId, posId)))

export const queueOfflineSale = async (
	payload,
	auth,
	offlineSaleId = crypto.randomUUID(),
) => {
	const offline_sale_id = offlineSaleId
	const created_at = new Date().toISOString()
	const temporary_receipt = `OFF-${offline_sale_id.slice(0, 8).toUpperCase()}`
	const record = toPlainData({
		...payload,
		offline_sale_id,
		created_at,
		temporary_receipt,
		cashier_id: auth?._id,
		status: "pending",
	})
	await run(OUTBOX_STORE, "readwrite", (store) => store.put(record))
	notifyOutboxChanged()
	return record
}

export const listOfflineSales = async () => {
	const db = await openDb()
	return new Promise((resolve, reject) => {
		const tx = db.transaction(OUTBOX_STORE, "readonly")
		const request = tx.objectStore(OUTBOX_STORE).getAll()
		request.onsuccess = () => resolve((request.result || []).sort((a, b) => a.created_at.localeCompare(b.created_at)))
		request.onerror = () => reject(request.error)
		tx.oncomplete = () => db.close()
	})
}

export const updateOfflineSale = async (sale) => {
	await run(OUTBOX_STORE, "readwrite", (store) => store.put(toPlainData(sale)))
	notifyOutboxChanged()
}

export const removeOfflineSale = async (offlineSaleId) => {
	await run(OUTBOX_STORE, "readwrite", (store) => store.delete(offlineSaleId))
	notifyOutboxChanged()
}

export const isOffline = (serverOnline = true) =>
	process.client && (navigator.onLine === false || serverOnline === false)
